import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import Logo from '../../components/Logo'
import Button from '../components/Button'
import Link from '../components/Link'
import Icon from '../components/Icon'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const { login, error, loading, clearError } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearError()
  }, [clearError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setEmailNotVerified(false)

    try {
      const response = await login({ email, password })

      // Check if 2FA is required
      if (response?.requires2FA) {
        navigate('/verify-2fa', { state: { email } })
      } else {
        navigate('/')
      }
    } catch (err: any) {
      // Check if error is due to unverified email
      if (err.response?.data?.emailNotVerified) {
        setEmailNotVerified(true)
      }
      console.error('Login error:', err)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <Logo />
          <h3 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            Sign in to your account
          </h3>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
              {emailNotVerified && (
                <div className="mt-2">
                  <Link
                    to="/resend-verification"
                    variant="danger"
                    className="underline"
                  >
                    Resend verification email
                  </Link>
                </div>
              )}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 z-20"
              >
                {showPassword ? (
                  <Icon type="eye-off" className="h-5 w-5" />
                ) : (
                  <Icon type="eye" className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          <div>
            <Button type="submit" variant="primary" fullWidth loading={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-sm">
              <Link to="/forgot-password">Forgot your password?</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
